import type { SelectionState } from '../type/common.type';
import type { ItemListMapValueType, OptionMapState, ShippingIdUpdate, ShippingTypeIds } from '../type/common.type';
import type { OptionData } from '../type/common.type';

interface CompleteOptihandleSelectionParams {
  option: OptionData;
  update_index: number;
  shipping_ids: ShippingTypeIds;
  option_map: OptionMapState | null;
  maxPurchaseQuantity?: number;
  item_card_list: Array<{ id: string; count: number }>;
  getCompleteSelection: (option: OptionData, update_index: number, shipping_ids: ShippingTypeIds) => SelectionState;
}

interface CompleteOptihandleSelectionResult {
  item_to_add?: ItemListMapValueType;
  should_show_duplicate_message: boolean;
  should_show_exceed_limit_message: boolean;
  complete_selection: SelectionState;
}

/**
 * 옵션 선택 완료 처리 핵심 로직
 */
export const completeOptihandleSelection = (
  params: CompleteOptihandleSelectionParams,
): CompleteOptihandleSelectionResult => {
  const { option, update_index, shipping_ids, option_map, maxPurchaseQuantity, item_card_list, getCompleteSelection } =
    params;

  const complete_selection = getCompleteSelection(option, update_index, shipping_ids);

  let item_list: ItemListMapValueType | undefined;

  if (complete_selection.metadata.last_selected_shipping_type === 'ZIGZIN') {
    item_list = option_map?.item_list_map?.zigzin.get(
      complete_selection.metadata.selected_zigzin_option_id_list.join(';'),
    );
  } else {
    item_list = option_map?.item_list_map?.matched.get(
      complete_selection.metadata.selected_general_option_id_list.join(';'),
    );
  }

  if (!item_list) {
    return {
      should_show_duplicate_message: false,
      should_show_exceed_limit_message: false,
      complete_selection,
    };
  }

  const existing_item = item_card_list.find((item) => item.id === item_list.id);
  const should_show_exceed_limit_message = !!(
    existing_item &&
    maxPurchaseQuantity &&
    existing_item.count >= maxPurchaseQuantity
  );

  if (should_show_exceed_limit_message) {
    return {
      should_show_duplicate_message: false,
      should_show_exceed_limit_message: true,
      complete_selection,
    };
  }

  return {
    item_to_add: item_list,
    should_show_duplicate_message: !!existing_item,
    should_show_exceed_limit_message: false,
    complete_selection,
  };
};

interface ContinueOptihandleSelectionParams {
  option: OptionData;
  update_index: number;
  shipping_ids: ShippingTypeIds;
}

/**
 * 옵션 선택 계속 진행 처리
 */
export const continueOptihandleSelection = (params: ContinueOptihandleSelectionParams) => {
  return {
    next_dropdown_index: params.update_index,
    selection_data: {
      option: params.option,
      shipping_ids: params.shipping_ids,
    },
  };
};

interface CompleteRestockNotificationParams {
  option: OptionData;
  update_index: number;
  option_map: OptionMapState | null;
  getCompleteSelection: (option: OptionData, update_index: number, shipping_ids: ShippingTypeIds) => SelectionState;
}

/**
 * 재입고 알림 선택 완료 처리
 */
export const completeRestockNotificatihandleSelection = (params: CompleteRestockNotificationParams) => {
  const { option, update_index, option_map, getCompleteSelection } = params;

  const complete_selection = getCompleteSelection(option, update_index, {
    zigzin_id: option.id,
    general_id: '',
  });

  const find_option = option_map?.path_index_map.zigzin.get(
    complete_selection.metadata.selected_option_name_list.join('/'),
  );

  return {
    product_item_id: find_option?.id,
    is_active: find_option?.wms_notification_info?.active ?? false,
  };
};

export const getCompleteSelection = (
  selection: SelectionState,
  option: OptionData,
  update_index: number,
  shipping_ids?: ShippingIdUpdate,
): SelectionState => {
  const { metadata } = selection;
  let zigzin_id_list = metadata.selected_zigzin_option_id_list;
  let general_id_list = metadata.selected_general_option_id_list;

  // 배송 타입별 ID가 제공된 경우 업데이트
  if (shipping_ids) {
    if (shipping_ids.zigzin_id) {
      zigzin_id_list = [...metadata.selected_zigzin_option_id_list, shipping_ids.zigzin_id];
    }
    if (shipping_ids.general_id) {
      general_id_list = [...metadata.selected_general_option_id_list, shipping_ids.general_id];
    }
  }

  return {
    selected_group_index: selection.selected_group_index,
    selected_dropdown_index: update_index,
    metadata: {
      selected_option_name_list: metadata.selected_option_name_list.concat(option.name),
      selected_zigzin_option_id_list: zigzin_id_list,
      selected_general_option_id_list: general_id_list,
      last_selected_shipping_type: option.shipping?.type || null,
      previous_group_selections: metadata.previous_group_selections,
    },
  };
};
